import _ from 'lodash';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  BinMode,
  ControlChartType,
  DEFAULT_ALIGNMENT_OPTION,
  DEFAULT_BIN_WIDTH,
  DEFAULT_BIN_WIDTH_UOM,
  DEFAULT_NUMBER_OF_BINS,
  DEFAULT_RATIONAL_SUBGROUP_SIZE,
} from '@/tools/statisticsSummary/statisticsSummary.constants';
import { BASE_TOOL_COMMON_PROPS, DEFAULT_TOOL_ITEM_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { RunRules } from '@/tools/statisticsSummary/statisticsSummary.constants';
import type {
  BaseToolPersistedState,
  ParameterDefinitions,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
  ToolRehydratePayload,
} from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { Capsule } from '@/utilities/datetime.constants';

type StatisticsConditionToolTime = ToolItem & Required<Pick<ToolItem, 'id'>>;
export interface StatisticsSummaryState extends BaseToolPersistedState {
  inputSignal?: StatisticsConditionToolTime;
  condition?: StatisticsConditionToolTime;
  alignmentCondition?: StatisticsConditionToolTime;
  alignmentMaximumDurationOverride?: ValueWithUnitsItem | undefined;
  /** Start of the time window the summary is computed over. */
  windowStart: number;
  /** End of the time window the summary is computed over. */
  windowEnd: number;
  color: string;
  binMode: BinMode;
  binWidth: number | null;
  binWidthUom: string;
  numberOfBins: number | null;
  upperSpecificationLimit: number | null;
  lowerSpecificationLimit: number | null;
  target: number | null;
  subgroupSize: number | null;
  controlChart?: ControlChartType;
  rationalSubgroupSize: number;
  runRules: RunRules[];
  showSpcChartSettings: boolean;
  manualAlignmentConditionName?: string;
  cpkSubgroupSizeIsManual: boolean;
}

export type StatisticsSummaryDehydratedState = StatisticsSummaryState;

export interface StatisticsSummaryActions {
  setSearchName: (name: string) => void;
  setAdvancedParametersCollapsed: (collapsed: boolean) => void;
  setInputSignal: (item: ToolItem | null | undefined) => void;
  unsetInputSignal: () => void;
  setCondition: (item: ToolItem | null | undefined) => void;
  setAlignmentCondition: (item: ToolItem | null | undefined) => void;
  setColor: (color: string) => void;
  /** Sets the sample window start/end together. */
  setSampleWindow: (window: Capsule) => void;
  setBinMode: (binMode: BinMode) => void;
  setBinWidth: (binWidth: number | null) => void;
  setBinWidthUom: (binWidthUom: string) => void;
  setNumberOfBins: (numberOfBins: number | null) => void;
  setUpperSpecificationLimit: (upperSpecificationLimit: number | null) => void;
  setLowerSpecificationLimit: (lowerSpecificationLimit: number | null) => void;
  setTarget: (target: number | null) => void;
  setSubgroupSize: (subgroupSize: number | null) => void;
  setControlChart: (controlChart: ControlChartType) => void;
  setRunRules: (runRules: RunRules[]) => void;
  setRationalSubgroupSize: (rationalSubgroupSize: number) => void;
  setShowSpcChartSettings: (showSpcChartSettings: boolean) => void;
  setManualAlignmentConditionName: (manualAlignmentConditionName: string | undefined) => void;
  setCpkSubgroupSizeIsManual: (cpkSubgroupSizeIsManual: boolean) => void;
  setAlignmentMaximumDurationOverride: (alignmentMaximumDurationOverride: ValueWithUnitsItem) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  reset: ResettableStoreState<StatisticsSummaryState>;
}

const parameterDefinitions: ParameterDefinitions<'inputSignal' | 'condition' | 'alignmentCondition'> = {
  condition: {
    predicate: ['name', 'condition'],
  },
  alignmentCondition: {
    predicate: ['name', 'alignmentCondition'],
  },
  inputSignal: {
    predicate: (parameter) => (parameter.name ?? '') !== 'condition' && (parameter.name ?? '') !== 'alignmentCondition',
  },
};

const PARAMETER_DEFINITION_KEYS = ['inputSignal', 'condition', 'alignmentCondition'];

const nextConditionToolItem = (item: ToolItem | null | undefined): StatisticsConditionToolTime | undefined =>
  (sanitizeToolItem(item, DEFAULT_TOOL_ITEM_PROPS) ?? undefined) as StatisticsConditionToolTime | undefined;

const initialState: StatisticsSummaryState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  inputSignal: undefined,
  condition: undefined,
  alignmentCondition: DEFAULT_ALIGNMENT_OPTION,
  alignmentMaximumDurationOverride: { value: 40, units: 'h' },
  windowStart: 0,
  windowEnd: 0,
  color: '',
  binMode: BinMode.SIZE,
  binWidth: DEFAULT_BIN_WIDTH,
  binWidthUom: DEFAULT_BIN_WIDTH_UOM,
  numberOfBins: DEFAULT_NUMBER_OF_BINS,
  upperSpecificationLimit: null,
  lowerSpecificationLimit: null,
  target: null,
  subgroupSize: null,
  controlChart: ControlChartType.IMR,
  runRules: [],
  rationalSubgroupSize: DEFAULT_RATIONAL_SUBGROUP_SIZE,
  showSpcChartSettings: false,
  manualAlignmentConditionName: undefined,
  cpkSubgroupSizeIsManual: false,
});

const statisticsSummaryWindowSelector = createSelector(
  [(state: StatisticsSummaryState) => state.windowStart, (state: StatisticsSummaryState) => state.windowEnd],
  (windowStart, windowEnd): Capsule => ({ startTime: windowStart, endTime: windowEnd }),
);

const statisticsSummaryConfigParamsSelector = createSelector(
  [(state: StatisticsSummaryState) => state],
  (state): ToolConfigParams =>
    getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.STATISTICS_SUMMARY,
      parameterDefinitionKeys: PARAMETER_DEFINITION_KEYS,
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    }),
);

export const dehydrateStatisticsSummaryState = (state: StatisticsSummaryState): StatisticsSummaryDehydratedState =>
  state;

const statisticsSummaryActionsFactory: ActionsFactory<StatisticsSummaryState, StatisticsSummaryActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setAdvancedParametersCollapsed: (collapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = collapsed;
    }),

  setInputSignal: (item) =>
    set((draft) => {
      const sanitizedItem = nextConditionToolItem(item);
      if (!_.isEqual(draft.inputSignal, sanitizedItem)) {
        draft.inputSignal = sanitizedItem;
      }
    }),

  unsetInputSignal: () =>
    set((draft) => {
      draft.inputSignal = undefined;
    }),

  setCondition: (item) =>
    set((draft) => {
      const sanitizedItem = nextConditionToolItem(item);
      if (!_.isEqual(draft.condition, sanitizedItem)) {
        draft.condition = sanitizedItem;
      }
    }),

  setAlignmentCondition: (item) =>
    set((draft) => {
      const sanitizedItem = nextConditionToolItem(item);
      if (!_.isEqual(draft.alignmentCondition, sanitizedItem)) {
        draft.alignmentCondition = sanitizedItem;
      }
    }),

  setColor: (color) =>
    set((draft) => {
      draft.color = color;
    }),

  setSampleWindow: (window) =>
    set((draft) => {
      draft.windowStart = window.startTime;
      draft.windowEnd = window.endTime;
    }),

  setBinMode: (binMode) =>
    set((draft) => {
      draft.binMode = binMode;
    }),

  setBinWidth: (binWidth) =>
    set((draft) => {
      draft.binWidth = binWidth;
    }),

  setBinWidthUom: (binWidthUom) =>
    set((draft) => {
      draft.binWidthUom = binWidthUom;
    }),

  setNumberOfBins: (numberOfBins) =>
    set((draft) => {
      draft.numberOfBins = numberOfBins;
    }),

  setUpperSpecificationLimit: (upperSpecificationLimit) =>
    set((draft) => {
      draft.upperSpecificationLimit = upperSpecificationLimit;
    }),

  setLowerSpecificationLimit: (lowerSpecificationLimit) =>
    set((draft) => {
      draft.lowerSpecificationLimit = lowerSpecificationLimit;
    }),

  setTarget: (target) =>
    set((draft) => {
      draft.target = target;
    }),

  setSubgroupSize: (subgroupSize) =>
    set((draft) => {
      draft.subgroupSize = subgroupSize;
    }),

  setControlChart: (controlChart) =>
    set((draft) => {
      draft.controlChart = controlChart;
    }),

  setRunRules: (runRules) =>
    set((draft) => {
      draft.runRules = runRules;
    }),

  setRationalSubgroupSize: (rationalSubgroupSize) =>
    set((draft) => {
      draft.rationalSubgroupSize = rationalSubgroupSize;
    }),

  setShowSpcChartSettings: (showSpcChartSettings) =>
    set((draft) => {
      draft.showSpcChartSettings = showSpcChartSettings;
    }),

  setManualAlignmentConditionName: (manualAlignmentConditionName) =>
    set((draft) => {
      draft.manualAlignmentConditionName = manualAlignmentConditionName;
    }),

  setCpkSubgroupSizeIsManual: (cpkSubgroupSizeIsManual) =>
    set((draft) => {
      draft.cpkSubgroupSizeIsManual = cpkSubgroupSizeIsManual;
    }),

  setAlignmentMaximumDurationOverride: (alignmentMaximumDurationOverride) =>
    set((draft) => {
      draft.alignmentMaximumDurationOverride = alignmentMaximumDurationOverride;
    }),

  rehydrateForEdit: (payload) => {
    resetState();
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });
      Object.assign(draft, rehydrationState.config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
      });
    });
  },

  reset: (overrides) => resetState(overrides),
});

const STORE_NAME = 'StatisticsSummary';

const [StatisticsSummaryStoreProvider, useStatisticsSummaryStoreInstance, useStatisticsSummarySelector] = createStore(
  STORE_NAME,
  initialState,
  statisticsSummaryActionsFactory,
);

export { StatisticsSummaryStoreProvider };

export const useStatisticsSummaryId = () => useStatisticsSummarySelector((state) => state.id);
export const useStatisticsSummaryName = () => useStatisticsSummarySelector((state) => state.name);
export const useStatisticsSummaryOriginalParameters = () =>
  useStatisticsSummarySelector((state) => state.originalParameters);
export const useStatisticsSummaryAdvancedParametersCollapsed = () =>
  useStatisticsSummarySelector((state) => state.advancedParametersCollapsed);
export const useStatisticsSummaryConfigVersion = () => useStatisticsSummarySelector((state) => state.configVersion);
export const useStatisticsSummaryInputSignal = () => useStatisticsSummarySelector((state) => state.inputSignal);
export const useStatisticsSummaryCondition = () => useStatisticsSummarySelector((state) => state.condition);
export const useStatisticsSummaryAlignmentCondition = () =>
  useStatisticsSummarySelector((state) => state.alignmentCondition);
export const useStatisticsSummaryAlignmentMaximumDurationOverride = () =>
  useStatisticsSummarySelector((state) => state.alignmentMaximumDurationOverride);
export const useStatisticsSummaryWindow = () => useStatisticsSummarySelector(statisticsSummaryWindowSelector);
export const useStatisticsSummaryColor = () => useStatisticsSummarySelector((state) => state.color);
export const useStatisticsSummaryBinMode = () => useStatisticsSummarySelector((state) => state.binMode);
export const useStatisticsSummaryBinWidth = () => useStatisticsSummarySelector((state) => state.binWidth);
export const useStatisticsSummaryBinWidthUom = () => useStatisticsSummarySelector((state) => state.binWidthUom);
export const useStatisticsSummaryNumberOfBins = () => useStatisticsSummarySelector((state) => state.numberOfBins);
export const useStatisticsSummaryUpperSpecificationLimit = () =>
  useStatisticsSummarySelector((state) => state.upperSpecificationLimit);
export const useStatisticsSummaryLowerSpecificationLimit = () =>
  useStatisticsSummarySelector((state) => state.lowerSpecificationLimit);
export const useStatisticsSummaryTarget = () => useStatisticsSummarySelector((state) => state.target);
export const useStatisticsSummarySubgroupSize = () => useStatisticsSummarySelector((state) => state.subgroupSize);
export const useStatisticsSummaryControlChart = () => useStatisticsSummarySelector((state) => state.controlChart);
export const useStatisticsSummaryRunRules = () => useStatisticsSummarySelector((state) => state.runRules);
export const useStatisticsSummaryRationalSubgroupSize = () =>
  useStatisticsSummarySelector((state) => state.rationalSubgroupSize);
export const useStatisticsSummaryShowSpcChartSettings = () =>
  useStatisticsSummarySelector((state) => state.showSpcChartSettings);
export const useStatisticsSummaryManualAlignmentConditionName = () =>
  useStatisticsSummarySelector((state) => state.manualAlignmentConditionName);
export const useStatisticsSummaryCpkSubgroupSizeIsManual = () =>
  useStatisticsSummarySelector((state) => state.cpkSubgroupSizeIsManual);
export const useStatisticsSummaryConfigParams = () =>
  useStatisticsSummarySelector(statisticsSummaryConfigParamsSelector);

export const useStatisticsSummaryActions = () => useStatisticsSummaryStoreInstance().actions;

/**
 * Creates an isolated store instance for tests that need direct state assertions.
 * Prefer provider-based tests unless a direct store instance is required.
 */
export function createTestStatisticsSummaryStore(overrides?: Partial<StatisticsSummaryState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestStatisticsSummaryStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, statisticsSummaryActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateStatisticsSummaryStore() {
  return createStoreInstance<StatisticsSummaryState, StatisticsSummaryActions>(
    initialState,
    statisticsSummaryActionsFactory,
    { storeName: STORE_NAME },
  );
}

export const usePersistStatisticsSummaryStore = () => {
  const register = useRegisterPersistedStore();
  const store = useStatisticsSummaryStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqStatisticsSummaryStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateStatisticsSummaryState,
    });
  }, [register, store]);
};
